/**
 * Typography Components
 * Strong hierarchy. Intentional sizing. No decoration.
 */

import { ReactNode, ElementType } from 'react';

interface TypographyProps {
  children: ReactNode;
  className?: string;
  as?: ElementType;
}

/* ========== Display (largest, hero text) ========== */

export function Display({ children, className = '', as: Tag = 'h1' }: TypographyProps) {
  return (
    <Tag className={`text-display-sm md:text-display font-medium tracking-tight text-text-primary text-balance ${className}`}>
      {children}
    </Tag>
  );
}

/* ========== Headline (page titles) ========== */

export function Headline({ children, className = '', as: Tag = 'h1' }: TypographyProps) {
  return (
    <Tag className={`text-h2 md:text-h1 font-medium tracking-tight text-text-primary ${className}`}>
      {children}
    </Tag>
  );
}

/* ========== Title (section titles) ========== */

export function Title({ children, className = '', as: Tag = 'h2' }: TypographyProps) {
  return (
    <Tag className={`text-h3 md:text-h2 font-medium tracking-tight text-text-primary ${className}`}>
      {children}
    </Tag>
  );
}

/* ========== Subtitle (subsection titles) ========== */

export function Subtitle({ children, className = '', as: Tag = 'h3' }: TypographyProps) {
  return (
    <Tag className={`text-h4 md:text-h3 font-medium text-text-primary ${className}`}>
      {children}
    </Tag>
  );
}

/* ========== Body Large (introductory text) ========== */

export function BodyLarge({ children, className = '', as: Tag = 'p' }: TypographyProps) {
  return (
    <Tag className={`text-base md:text-body-lg text-text-secondary leading-relaxed ${className}`}>
      {children}
    </Tag>
  );
}

/* ========== Body (standard text) ========== */

export function Body({ children, className = '', as: Tag = 'p' }: TypographyProps) {
  return (
    <Tag className={`text-base text-text-secondary leading-relaxed ${className}`}>
      {children}
    </Tag>
  );
}

/* ========== Label (uppercase, tracking) ========== */

export function Label({ children, className = '' }: TypographyProps) {
  return (
    <span className={`text-caption font-medium uppercase tracking-wider text-text-tertiary ${className}`}>
      {children}
    </span>
  );
}

/* ========== Small (fine print) ========== */

export function Small({ children, className = '', as: Tag = 'p' }: TypographyProps) {
  return (
    <Tag className={`text-small text-text-tertiary ${className}`}>
      {children}
    </Tag>
  );
}
